import { environment } from '@/constants/environment'

declare global {
  interface Window {
    google: any
    __googleMapsPromise?: Promise<void>
  }
}

const SCRIPT_ID = 'google-maps-sdk'

export function isGoogleMapsLoaded(): boolean {
  return typeof window !== 'undefined' && !!window.google?.maps?.Map
}

export function loadGoogleMaps(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps cannot load outside the browser'))
  }

  if (isGoogleMapsLoaded()) {
    return Promise.resolve()
  }

  if (window.__googleMapsPromise) {
    return window.__googleMapsPromise
  }

  const apiKey = environment.thirdParty.googleMapsKey
  if (!apiKey) {
    // Vite reads env at startup — a `.env.*` edit without a dev-server restart
    // leaves this undefined and the map silently disabled. Surface that loudly.
    console.error(
      '[Google Maps] VITE_GOOGLE_MAPS_KEY is not set. ' +
        'Add it to .env.development (must use the VITE_ prefix) and restart `npm run dev`.'
    )
    return Promise.reject(new Error('VITE_GOOGLE_MAPS_KEY is not set'))
  }

  window.__googleMapsPromise = new Promise<void>((resolve, reject) => {
    // Polls for the Map class — script.onload doesn't always coincide with
    // globals being attached, especially when ad-blockers stub parts of the SDK.
    const POLL_MS = 50
    const TIMEOUT_MS = 10_000
    const start = Date.now()
    const waitForReady = () => {
      if (isGoogleMapsLoaded()) {
        resolve()
        return
      }
      if (Date.now() - start > TIMEOUT_MS) {
        window.__googleMapsPromise = undefined
        reject(new Error('Google Maps SDK loaded but Map class never became available'))
        return
      }
      setTimeout(waitForReady, POLL_MS)
    }

    const onError = () => {
      window.__googleMapsPromise = undefined
      reject(new Error('Google Maps script failed to load'))
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', waitForReady)
      existing.addEventListener('error', onError)
      waitForReady()
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`
    script.async = true
    script.defer = true
    script.onload = waitForReady
    script.onerror = onError
    document.head.appendChild(script)
  })

  return window.__googleMapsPromise
}
